/**
 * MACD from a close series — fast EMA minus slow EMA, then a signal EMA.
 *
 * Defaults are the standard 12 / 26 / 9. Missing legs stay null (R2b).
 */
import { MACD_FAST, MACD_SIGNAL, MACD_SLOW } from '../constants/moving-average';

import { exponentialMovingAverage } from './exponential-average';
import { requireAveragePeriod } from './moving-average';

export interface MacdSeries {
  readonly macd: readonly (number | null)[];
  readonly signal: readonly (number | null)[];
  readonly histogram: readonly (number | null)[];
}

function subtractAligned(
  left: readonly (number | null)[],
  right: readonly (number | null)[],
): readonly (number | null)[] {
  return left.map((value, index) => {
    const other = right[index];
    if (value === null || other === null || other === undefined) return null;
    return value - other;
  });
}

function nanForNull(values: readonly (number | null)[]): readonly number[] {
  return values.map((value) => value ?? Number.NaN);
}

export function macdSeries(
  bars: readonly number[],
  fast: number = MACD_FAST,
  slow: number = MACD_SLOW,
  signalPeriod: number = MACD_SIGNAL,
): MacdSeries {
  requireAveragePeriod(fast);
  requireAveragePeriod(slow);
  requireAveragePeriod(signalPeriod);
  if (fast >= slow) {
    throw new RangeError(
      `MACD fast period must be less than slow (got ${String(fast)}, ${String(slow)})`,
    );
  }
  const macd = subtractAligned(
    exponentialMovingAverage(bars, fast),
    exponentialMovingAverage(bars, slow),
  );
  const signal = exponentialMovingAverage(nanForNull(macd), signalPeriod);
  return { macd, signal, histogram: subtractAligned(macd, signal) };
}
